import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMemo } from "react";
import { musicApi } from "@/api/music";
import type { Album, Artist } from "@/api/types";
import { TagPills } from "@/components/TagPills";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";
import { PosterTile } from "./ArtistList";

export function ArtistDetail() {
  const { id } = useParams({ from: "/music/artists/$id" });
  const artistId = Number(id);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data, isLoading } = useQuery({
    queryKey: ["music", "artist", artistId],
    queryFn: () => musicApi.getArtist(artistId),
  });

  const monitorMut = useMutation({
    mutationFn: (monitored: boolean) =>
      musicApi.updateArtist(artistId, { monitored }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["music", "artist", artistId] });
      qc.invalidateQueries({ queryKey: ["music", "artists"] });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : "Update failed", "error"),
  });

  const removeMut = useMutation({
    mutationFn: () => musicApi.deleteArtist(artistId),
    onSuccess: () => {
      toast("Artist removed", "success");
      qc.invalidateQueries({ queryKey: ["music", "artists"] });
      navigate({ to: "/music" });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : "Remove failed", "error"),
  });

  // Issue #6 — manual MusicBrainz refresh. Re-fetches the artist's
  // release-group list, UPDATEs existing albums in place, and
  // INSERTs any newly-announced albums monitored to match the artist.
  const refreshMut = useMutation({
    mutationFn: () => musicApi.refreshArtist(artistId),
    onSuccess: (resp) => {
      const msg =
        resp.new_albums > 0
          ? `Refreshed; ${resp.new_albums} new album${resp.new_albums === 1 ? "" : "s"} added`
          : "Refreshed; no new albums";
      toast(msg, "success");
      qc.invalidateQueries({ queryKey: ["music", "artist", artistId] });
      qc.invalidateQueries({ queryKey: ["music", "artists"] });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : "Refresh failed", "error"),
  });

  const sortedAlbums = useMemo(() => {
    const list = data?.albums ?? [];
    // Release date desc, nulls last.
    return list.slice().sort((a, b) => {
      const ad = a.release_date;
      const bd = b.release_date;
      if (!ad && !bd) return a.title.localeCompare(b.title);
      if (!ad) return 1;
      if (!bd) return -1;
      return bd.localeCompare(ad);
    });
  }, [data?.albums]);

  if (isLoading) {
    return <p className="text-sm text-foreground/60">Loading…</p>;
  }
  if (!data) {
    return <p className="text-sm text-foreground/60">Not found.</p>;
  }

  const { artist } = data;
  const downloadedCount = sortedAlbums.filter((al) => al.has_file).length;

  return (
    <div className="space-y-6">
      {/* Fanart hero — wide 16:9 backdrop with the artist name + actions
       * overlaid. Issue #12. NULL fanart falls through to the regular
       * `<PageHeader>` below; we render one or the other, not both, to
       * keep the visual hierarchy consistent. The Refresh button (#6)
       * lives on both branches. */}
      {artist.fanart_url ? (
        <FanartHero
          artist={artist}
          onRefresh={() => refreshMut.mutate()}
          refreshLoading={refreshMut.isPending}
          onMonitor={() => monitorMut.mutate(!artist.monitored)}
          monitorLoading={monitorMut.isPending}
          onDelete={() => {
            if (
              confirm(
                `Remove "${artist.name}" and all of their albums from your library?`,
              )
            ) {
              removeMut.mutate();
            }
          }}
          deleteLoading={removeMut.isPending}
        />
      ) : (
        <PageHeader
          title={artist.name}
          description={artist.overview ?? undefined}
          actions={
            <>
              <Button
                variant="secondary"
                onClick={() => refreshMut.mutate()}
                loading={refreshMut.isPending}
              >
                Refresh
              </Button>
              <Button
                variant="secondary"
                onClick={() => monitorMut.mutate(!artist.monitored)}
                loading={monitorMut.isPending}
              >
                {artist.monitored ? "Unmonitor" : "Monitor"}
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  if (
                    confirm(
                      `Remove "${artist.name}" and all of their albums from your library?`,
                    )
                  ) {
                    removeMut.mutate();
                  }
                }}
                loading={removeMut.isPending}
              >
                Delete
              </Button>
            </>
          }
        />
      )}

      <div className="flex flex-col gap-6 sm:grid sm:grid-cols-[12rem_1fr]">
        <div className="w-36 self-center sm:w-48 sm:self-auto">
          <div className="overflow-hidden rounded border border-line">
            <PosterTile
              kind="artists"
              id={artist.id}
              url={artist.poster_url}
              name={artist.sort_name || artist.name}
              ratio="2/3"
            />
          </div>
        </div>
        <div className="space-y-4 text-sm">
          <dl className="grid grid-cols-[10rem_1fr] gap-y-1 text-xs text-foreground/70">
            <dt className="text-foreground/50">MusicBrainz ID</dt>
            <dd className="font-mono">{artist.mbid}</dd>
            <dt className="text-foreground/50">Path</dt>
            <dd className="font-mono">{artist.path ?? "—"}</dd>
            <dt className="text-foreground/50">Monitored</dt>
            <dd>{artist.monitored ? "Yes" : "No"}</dd>
            <dt className="text-foreground/50">Albums</dt>
            <dd>
              {sortedAlbums.length}{" "}
              <span className="text-foreground/50">
                ({downloadedCount} downloaded)
              </span>
            </dd>
            <dt className="text-foreground/50">Added</dt>
            <dd>{new Date(artist.created_at).toLocaleDateString()}</dd>
            <dt className="text-foreground/50">Updated</dt>
            <dd>{new Date(artist.updated_at).toLocaleDateString()}</dd>
          </dl>
        </div>
      </div>

      {/* Issue #6 — surface the periodic-refresh cursor so the user
          can verify the loop is doing work. Muted on purpose: this
          is informational, not a primary action. */}
      <p className="text-xs text-foreground/50">
        Refreshed {formatRefreshedAt(artist.last_refreshed_at)}
      </p>

      <TagPills
        target={{ kind: "media", mediaKind: "artist", id: artist.id }}
        showEmpty
      />

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-foreground/80">
          Albums ({sortedAlbums.length})
        </h2>
        {sortedAlbums.length === 0 ? (
          <EmptyState
            title="No albums"
            description="The MusicBrainz import didn't return any release groups for this artist."
          />
        ) : (
          <div className="overflow-x-auto rounded border border-line">
            <table className="w-full text-sm">
              <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
                <tr>
                  <th className="w-12 px-3 py-2"></th>
                  <th className="px-3 py-2 font-medium">Title</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium tabular-nums">Year</th>
                  <th className="px-3 py-2 font-medium">Monitored</th>
                  <th className="px-3 py-2 font-medium">File</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {sortedAlbums.map((album) => (
                  <AlbumRow key={album.id} album={album} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function AlbumRow({ album }: { album: Album }) {
  const qc = useQueryClient();
  const { toast } = useToast();

  const monitorMut = useMutation({
    mutationFn: (monitored: boolean) =>
      musicApi.updateAlbum(album.id, { monitored }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["music", "artist", album.artist_id] });
      qc.invalidateQueries({ queryKey: ["music", "album", album.id] });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : "Update failed", "error"),
  });

  const searchMut = useMutation({
    mutationFn: () => musicApi.searchAlbum(album.id),
    onSuccess: (record) => {
      toast(
        `Grabbed ${record.release_title} via ${record.indexer_name ?? "?"} → ${record.download_client_name ?? "?"}`,
        "success",
      );
      qc.invalidateQueries({ queryKey: ["grabs"] });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : "Grab failed", "error"),
  });

  const deleteMut = useMutation({
    mutationFn: () => musicApi.deleteAlbum(album.id),
    onSuccess: () => {
      toast(`Deleted "${album.title}"`, "success");
      qc.invalidateQueries({ queryKey: ["music", "artist", album.artist_id] });
      qc.invalidateQueries({ queryKey: ["music", "stats"] });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : "Delete failed", "error"),
  });

  const year = parseYear(album.release_date);
  const secondaryTypes = parseSecondaryTypes(album.secondary_types);

  return (
    <tr className="border-t border-line">
      <td className="px-3 py-2">
        <Link
          to="/music/albums/$id"
          params={{ id: String(album.id) }}
          className="block w-10 overflow-hidden rounded border border-line"
        >
          <PosterTile
            kind="albums"
            id={album.id}
            url={album.poster_url}
            name={album.title}
            ratio="1/1"
          />
        </Link>
      </td>
      <td className="px-3 py-2">
        <Link
          to="/music/albums/$id"
          params={{ id: String(album.id) }}
          className="font-medium hover:text-accent"
        >
          {album.title}
          {album.disambiguation && (
            // "Kid A Mnesia (compilation)" — small + muted so the
            // disambiguation never crowds the actual title. NULL =
            // canonical release, no parens at all.
            <span className="ml-1 text-xs font-normal text-foreground/50">
              ({album.disambiguation})
            </span>
          )}
        </Link>
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap items-center gap-1">
          {album.primary_type ? (
            <span className="rounded bg-surface-2/60 px-1.5 py-0.5 text-[10px] uppercase text-foreground/70">
              {album.primary_type}
            </span>
          ) : (
            <span className="text-foreground/40">—</span>
          )}
          {secondaryTypes.map((t) => (
            <span
              key={t}
              className="rounded bg-yellow-500/20 px-1.5 py-0.5 text-[10px] uppercase text-yellow-300"
            >
              {t}
            </span>
          ))}
        </div>
      </td>
      <td className="px-3 py-2 text-xs tabular-nums text-foreground/70">
        {year ?? "—"}
      </td>
      <td className="px-3 py-2 text-xs">
        <button
          type="button"
          onClick={() => monitorMut.mutate(!album.monitored)}
          disabled={monitorMut.isPending}
          className={cn(
            "rounded px-1.5 py-0.5 transition",
            album.monitored
              ? "text-foreground hover:bg-surface-2/60"
              : "text-foreground/50 hover:text-foreground hover:bg-surface-2/60",
          )}
        >
          {album.monitored ? "Yes" : "No"}
        </button>
      </td>
      <td className="px-3 py-2">
        <DownloadedIndicator hasFile={album.has_file} path={album.path} />
      </td>
      <td className="space-x-2 px-3 py-2 text-right">
        <Button
          size="sm"
          variant="secondary"
          loading={searchMut.isPending}
          onClick={() => searchMut.mutate()}
        >
          Search & Grab
        </Button>
        <Link to="/music/albums/$id" params={{ id: String(album.id) }}>
          <Button size="sm" variant="ghost">
            View
          </Button>
        </Link>
        <Button
          size="sm"
          variant="ghost"
          loading={deleteMut.isPending}
          onClick={() => {
            if (
              confirm(
                `Remove "${album.title}" from your library? Files on disk are NOT deleted.`,
              )
            ) {
              deleteMut.mutate();
            }
          }}
        >
          Delete
        </Button>
      </td>
    </tr>
  );
}

/**
 * Compact "downloaded" indicator. Shared between album rows on the artist
 * page and track rows on the album page. When `hasFile`, surfaces the path
 * via the native title tooltip.
 */
export function DownloadedIndicator({
  hasFile,
  path,
}: {
  hasFile: boolean;
  path: string | null;
}) {
  if (hasFile) {
    return (
      <span
        className="text-green-400"
        title={path ? `On disk: ${path}` : "Downloaded"}
        aria-label="Downloaded"
      >
        ✓
      </span>
    );
  }
  return (
    <span
      className="text-foreground/30"
      title="Not downloaded"
      aria-label="Not downloaded"
    >
      —
    </span>
  );
}

/**
 * Wide 16:9 backdrop sourced from fanart.tv (issue #12). Renders the
 * artist name + monitor / delete actions overlaid on the image with a
 * subtle gradient so the title stays legible across the full range of
 * fanart contrasts. If `artist.logo_url` is set we composite the
 * transparent clearlogo over the top-left corner so the page feels
 * closer to a Plex / Roon hero treatment; otherwise we fall back to
 * plain text.
 */
function FanartHero({
  artist,
  onRefresh,
  refreshLoading,
  onMonitor,
  monitorLoading,
  onDelete,
  deleteLoading,
}: {
  artist: Artist;
  onRefresh: () => void;
  refreshLoading: boolean;
  onMonitor: () => void;
  monitorLoading: boolean;
  onDelete: () => void;
  deleteLoading: boolean;
}) {
  return (
    <div className="relative -mx-4 -mt-2 overflow-hidden rounded-b border-b border-line sm:-mx-6 lg:-mx-8">
      <div className="aspect-[16/6] w-full bg-surface-2">
        <img
          src={artist.fanart_url ?? undefined}
          alt=""
          className="h-full w-full object-cover"
          loading="eager"
        />
      </div>
      {/* Gradient overlay — keeps the bottom-left title block readable
       * over both light and dark fanart. */}
      <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/70 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 px-6 pb-6">
        <div className="min-w-0">
          {artist.logo_url ? (
            <img
              src={artist.logo_url}
              alt={artist.name}
              className="mb-2 max-h-20 max-w-[20rem] object-contain object-left drop-shadow"
              loading="eager"
            />
          ) : (
            <h1 className="truncate text-3xl font-semibold drop-shadow">
              {artist.name}
            </h1>
          )}
          {artist.overview && (
            <p className="mt-1 line-clamp-2 max-w-2xl text-sm text-foreground/80">
              {artist.overview}
            </p>
          )}
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            variant="secondary"
            onClick={onRefresh}
            loading={refreshLoading}
          >
            Refresh
          </Button>
          <Button
            variant="secondary"
            onClick={onMonitor}
            loading={monitorLoading}
          >
            {artist.monitored ? "Unmonitor" : "Monitor"}
          </Button>
          <Button variant="danger" onClick={onDelete} loading={deleteLoading}>
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

function parseYear(date: string | null): number | null {
  if (!date) return null;
  const m = /^(\d{4})/.exec(date);
  return m ? Number(m[1]) : null;
}

/**
 * Render `last_refreshed_at` as a relative "Xh ago" / "Yd ago" string.
 * Returns `"never"` for NULL — both legacy artists imported before
 * the column was added and brand-new ones the loop hasn't walked yet
 * read the same way. The exact ISO timestamp lives in the column's
 * native title tooltip on hover.
 */
function formatRefreshedAt(iso: string | null): string {
  if (!iso) return "never";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "never";
  const elapsedMs = Math.max(0, Date.now() - t);
  const minutes = Math.floor(elapsedMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/**
 * Parse the JSON-encoded `secondary_types` column into a tag list.
 * Returns `[]` for NULL / malformed input — silent rather than
 * throwing because a stale DB value shouldn't take down the row
 * render.
 */
function parseSecondaryTypes(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((v): v is string => typeof v === "string");
    }
  } catch {
    /* fall through */
  }
  return [];
}
